import React from "react";
import {
  Box,
  Typography,
  useTheme,
  Icon,
  alpha,
  IconButton,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: string;
  actions?: React.ReactNode;
  backButton?: boolean;
  onBack?: () => void;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon,
  actions,
  backButton,
  onBack,
}) => {
  const theme = useTheme();
  const themeColor = "#0891B2";

  return (
    <Box sx={{ mb: 3, position: "relative" }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ position: "relative" }}
        >
          {/* Left Section */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={3}
            sx={{ zIndex: 1 }}
          >
            {/* Back Button */}
            {backButton && (
              <motion.div
                initial={{ x: -4, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                <IconButton
                  onClick={onBack}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: themeColor,
                      transform: "translateX(-2px)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Icon>arrow_back</Icon>
                </IconButton>
              </motion.div>
            )}

            {/* Title Area */}
            <Stack spacing={0.5}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                    color: "text.primary",
                    letterSpacing: "-0.01em",
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1.5,
                    pl: 1,
                  }}
                >
                  {title}
                </Typography>
              </motion.div>

              {subtitle && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontFamily: "Montserrat",
                      fontWeight: 500,
                      pl: 1,
                    }}
                  >
                    {subtitle}
                  </Typography>
                </motion.div>
              )}
            </Stack>
          </Stack>

          {/* Actions */}
          {actions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  "& .MuiButton-root": {
                    textTransform: "none",
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    minHeight: 42,
                    px: 2,
                    transition: "all 0.2s",
                    "&:not(.MuiButton-contained)": {
                      bgcolor: "transparent",
                      color: themeColor,
                      border: `1px solid ${alpha(themeColor, 0.2)}`,
                      "&:hover": {
                        bgcolor: alpha(themeColor, 0.04),
                        border: `1px solid ${alpha(themeColor, 0.4)}`,
                      },
                    },
                    "&.MuiButton-contained": {
                      background: themeColor,
                      boxShadow: "none",
                      "&:hover": {
                        background: themeColor,
                        boxShadow: `0 8px 16px ${alpha(themeColor, 0.16)}`,
                        transform: "translateY(-1px)",
                      },
                    },
                  },
                }}
              >
                {actions}
              </Box>
            </motion.div>
          )}
        </Stack>

        {/* Bottom Line */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            sx={{
              height: 1,
              background: (theme) => theme.palette.divider,
              opacity: 0.6,
            }}
          />
        </motion.div>
      </Stack>
    </Box>
  );
};
